/** @jsxImportSource @emotion/react */
"use client";

import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { MediaSizes } from "@packages/media";
import { usePlayer } from "@packages/media";
import { Button as ToolbarButton } from "@radix-ui/react-toolbar";
import type { MouseEventHandler } from "react";
import { useMemo } from "react";

import { useMediaAnalyticsProps } from "../../../hooks/useMediaAnalyticsProps";
import {
  coreMediaStyles,
  settingsContainerStyles,
  useMediaClass,
} from "../../../utils/media.emotion";
import { VolumeControl } from "../controls/VolumeControl.emotion";
import { SettingsControls } from "./SettingsControls/SettingsControls.emotion";

export type SettingsContainerProps = {
  size: MediaSizes;
  onMouseMove: MouseEventHandler;
  showControls?: boolean;
};

export const SettingsContainer = ({
  size,
  onMouseMove,
  showControls,
}: SettingsContainerProps) => {
  const player = usePlayer();
  const mediaClass = useMediaClass();
  const { track } = useAnalytics();
  const analyticsProps = useMediaAnalyticsProps();

  const containerStyle = useMemo(
    () => settingsContainerStyles[mediaClass],
    [mediaClass],
  );

  return (
    <div
      key={"settingsContainer"}
      css={[
        containerStyle,
        coreMediaStyles.edgeBase,
        coreMediaStyles.bottomEdge,
        coreMediaStyles.unclickable,
        showControls ? coreMediaStyles.edgeVisible : null,
      ]}
      onMouseMove={onMouseMove}
    >
      <SettingsControls
        Wrapper={ToolbarButton}
        withVolume={
          (mediaClass.includes("expanded") ||
            mediaClass.includes("fullscreen")) &&
          (mediaClass.includes("desktop") ||
            (mediaClass.includes("landscape") && mediaClass.includes("audio")))
        }
        size={size === "expanded-mobile" ? "s" : "l"}
        showControls={showControls}
      />
      {size === "collapsed-desktop" ? (
        <VolumeControl
          iconColor={"onColorVeryHigh"}
          value={player?.volume ?? 1}
          onVolumeChange={(v) => player?.setVolume(v)}
          onVolumeCommit={(v) =>
            track({
              event: HallowAnalyticsEvent.ChangedSessionVolume,
              properties: {
                ...analyticsProps,
                volume: v,
              },
            })
          }
          onOverlay={true}
          Wrapper={ToolbarButton}
        />
      ) : null}
    </div>
  );
};
